import { useGetVideo } from '@vimeo/gctl-api/hooks/useVideo';
import { VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { useGetAlbumVideoData } from '@vimeo/use-get-album-video';
import { useGetUnlockedVideo } from '@vimeo/use-unlocked-video';

import { useViewer } from './use-viewer';
import { VIDEO_DATA_FIELDS } from '../utils/constants';
import {
  extractClipData,
  getReviewPasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  mapToClipFields,
} from '../utils/helper';

export const useVideoData = (clipRequestId: string, showcaseId?: number, reviewId?: string) => {
  const { data, isLoading, ...gctlConfig } = useGetUnlockedVideoData(
    clipRequestId,
    showcaseId,
    reviewId,
  );

  return {
    videoData: data,
    videoDataLoading: isLoading || !data,
    ...gctlConfig,
  };
};

/**
 * Custom hook to fetch video data based on the context (regular video, review link, or showcase)
 */
const useGetUnlockedVideoData = (clipRequestId: string, showcaseId?: number, reviewId?: string) => {
  const viewer = useViewer();

  const reviewVideoData = useGetVideo(
    // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      if (!viewer || !clipRequestId || !reviewId || showcaseId) return null;

      const password = getReviewPasswordHashFromCookie(reviewId);
      return {
        where: { videoId: clipRequestId },
        select: VIDEO_DATA_FIELDS,
        query: { reviewId, password },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  const albumVideoData = useGetAlbumVideoData(
    showcaseId ? showcaseId : null,
    Number(getVideoIdFromClipRequestId(clipRequestId)),
    mapToClipFields(VIDEO_DATA_FIELDS as VideoFields[]),
  );

  const videoData = useGetUnlockedVideo(
    // @ts-expect-error videoId expects a number, but doesn't account for clipHash strings
    () => {
      if (!viewer || showcaseId || reviewId) return null;

      return {
        where: { videoId: clipRequestId },
        select: VIDEO_DATA_FIELDS,
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  if (reviewId) {
    return reviewVideoData;
  }

  if (showcaseId) {
    return {
      ...albumVideoData,
      data: extractClipData(albumVideoData.data as Record<string, unknown>),
      isLoading: albumVideoData.isLoading,
    };
  }

  return videoData;
};
